import { IconKey } from '@qred/qit-ui';
import { CountryCode } from '~/enums';
import { IValueDefaults } from '~/Interfaces';
import { Language, LanguageLabel } from '~/store/types/sharedTypes';
import { PartialRecord } from '~/types/types';
import constVars from './constVars';

type MarketValues<T> = Record<CountryCode, T>;
export type LanguageValues = Record<Language, string>;
export type MarketLanguagePartial = Record<
  CountryCode,
  Partial<Record<Language, string>>
>;

export const homePageByMarket: Record<
  CountryCode,
  PartialRecord<Language, string>
> = {
  [CountryCode.SE]: {
    [Language.Swedish]: 'https://www.qred.se',
    [Language.English]: 'https://www.qred.se/en',
  },
  [CountryCode.FI]: {
    [Language.Finnish]: 'https://www.qred.fi',
    [Language.English]: 'https://www.qred.fi/en',
  },
  [CountryCode.NO]: {
    [Language.Norwegian]: 'https://www.qred.no',
    [Language.English]: 'https://www.qred.no/en',
  },
  [CountryCode.BE]: {
    [Language.DutchBE]: 'https://www.qred.be/nl',
    [Language.French]: 'https://www.qred.be/fr',
    [Language.English]: 'https://www.qred.be/en',
  },
  [CountryCode.DK]: {
    [Language.Danish]: 'https://www.qred.dk',
    [Language.English]: 'https://www.qred.dk/en',
  },
  [CountryCode.NL]: {
    [Language.Dutch]: 'https://www.qeld.nl',
  },
  [CountryCode.DE]: {
    [Language.German]: 'https://www.qred.de',
    [Language.English]: 'https://www.qred.de/en',
  },
};

export const countryNames: MarketLanguagePartial = {
  [CountryCode.BE]: {
    [Language.French]: 'Belgique',
    [Language.DutchBE]: 'België',
    [Language.English]: 'Belgium',
  },
  [CountryCode.DK]: {
    [Language.Danish]: 'Danmark',
    [Language.English]: 'Denmark',
  },
  [CountryCode.FI]: {
    [Language.Finnish]: 'Suomi',
    [Language.English]: 'Finland',
  },
  [CountryCode.SE]: {
    [Language.Swedish]: 'Sverige',
    [Language.English]: 'Sweden',
  },
  [CountryCode.NO]: {
    [Language.Norwegian]: 'Norge',
    [Language.English]: 'Norway',
  },
  [CountryCode.NL]: {
    [Language.Dutch]: 'Nederland',
    [Language.English]: 'Netherlands',
  },
  [CountryCode.DE]: {
    [Language.German]: 'Deutschland',
    [Language.English]: 'Germany',
  },
};

const validLanguagesBE: Array<Language> = [
  Language.DutchBE,
  Language.French,
  Language.English,
];
const validLanguagesDK: Array<Language> = [Language.Danish, Language.English];
const validLanguagesSE: Array<Language> = [Language.Swedish, Language.English];
const validLanguagesNO: Array<Language> = [
  Language.Norwegian,
  Language.English,
];
const validLanguagesFI: Array<Language> = [Language.Finnish, Language.English];
const validLanguagesNL: Array<Language> = [Language.Dutch];
const validLanguagesDE: Array<Language> = [Language.German, Language.English];

export const ValidLanguagesPerMarket: Record<CountryCode, Array<Language>> = {
  [CountryCode.BE]: validLanguagesBE,
  [CountryCode.DK]: validLanguagesDK,
  [CountryCode.SE]: validLanguagesSE,
  [CountryCode.NO]: validLanguagesNO,
  [CountryCode.FI]: validLanguagesFI,
  [CountryCode.NL]: validLanguagesNL,
  [CountryCode.DE]: validLanguagesDE,
};

export const languageLabels: Record<Language, LanguageLabel> = {
  [Language.DutchBE]: {
    long: 'Nederlands',
    short: 'NL',
  },
  [Language.Dutch]: {
    long: 'Nederlands',
    short: 'NL',
  },
  [Language.French]: {
    long: 'Français',
    short: 'FR',
  },
  [Language.Danish]: {
    long: 'Dansk',
    short: 'DA',
  },
  [Language.Swedish]: {
    long: 'Svenska',
    short: 'SV',
  },
  [Language.Norwegian]: {
    long: 'Norsk',
    short: 'NO',
  },
  [Language.Finnish]: {
    long: 'Suomi',
    short: 'FI',
  },
  [Language.English]: {
    long: 'English',
    short: 'EN',
  },
  [Language.German]: {
    long: 'German',
    short: 'DE',
  },
};

const languageIcons: Record<Language, IconKey> = {
  [Language.DutchBE]: 'FlagBelgium',
  [Language.Dutch]: 'FlagNetherlands',
  [Language.French]: 'FlagFrance',
  [Language.Danish]: 'FlagDenmark',
  [Language.Swedish]: 'FlagSweden',
  [Language.Norwegian]: 'FlagNorway',
  [Language.Finnish]: 'FlagFinland',
  [Language.English]: 'FlagUnitedKingdom',
  [Language.German]: 'FlagGermany',
};

export const getMarketLanguageLabels = (market: CountryCode) =>
  Object.values(ValidLanguagesPerMarket[market]).map((validLang: Language) => ({
    label: languageLabels[validLang],
    value: validLang,
    icon: languageIcons[validLang],
  }));

export const ContactNumbers: MarketValues<string> = {
  [CountryCode.DK]: '89871006',
  [CountryCode.FI]: '0942450399',
  [CountryCode.NL]: '0208086084',
  [CountryCode.SE]: '020150333',
  [CountryCode.BE]: '025880983',
  [CountryCode.NO]: '32993641',
  [CountryCode.DE]: '040228999808',
};

export const ContactNumbersFormatted: MarketValues<string> = {
  [CountryCode.DK]: '89 87 10 06',
  [CountryCode.FI]: '09 424 503 99 (pvm/mpm)',
  [CountryCode.NL]: '020 - 808 6084',
  [CountryCode.SE]: '020-150 333',
  [CountryCode.BE]: '02 588 09 83',
  [CountryCode.NO]: '32 99 36 41',
  [CountryCode.DE]: '040 228999808',
};

export const ContactEmails: MarketValues<string> = {
  [CountryCode.DK]: 'support@qred.dk',
  [CountryCode.FI]: 'asiakaspalvelu@qred.com',
  [CountryCode.NL]: 'support@qeld.nl',
  [CountryCode.SE]: 'support@qred.se',
  [CountryCode.BE]: 'support@qred.be',
  [CountryCode.NO]: 'support@qred.no',
  [CountryCode.DE]: 'support@qred.de',
};

export const CreditSafeContactEmails: Partial<MarketValues<string>> = {
  [CountryCode.DE]: 'datenaktualisierung@creditsafede.com',
};

const PersonalNumberPlaceholders: MarketLanguagePartial = {
  [CountryCode.BE]: {
    [Language.English]: 'XX.XX.XX-XXX.XX',
    [Language.French]: 'XX.XX.XX-XXX.XX',
    [Language.DutchBE]: 'XX.XX.XX-XXX.XX',
  },
  [CountryCode.DK]: {
    [Language.Danish]: 'DDMMÅÅ-NNNN',
    [Language.English]: 'DDMMYY-NNNN',
  },
  [CountryCode.FI]: {
    [Language.English]: 'DDMMYYCZZZQ',
    [Language.Finnish]: 'PPKKVVXNNNT',
  },
  [CountryCode.SE]: {
    [Language.English]: 'YYYYMMDD-XXXX',
    [Language.Swedish]: 'ÅÅÅÅMMDD-XXXX',
  },
  [CountryCode.NO]: {
    [Language.English]: 'DDMMYYXXXZZ',
    [Language.Norwegian]: 'DDMMÅÅXXXZZ',
  },
  [CountryCode.NL]: {
    [Language.English]: 'DDMMYYXXXZZ',
    [Language.Dutch]: 'DDMMÅÅXXXZZ',
  },
  [CountryCode.DE]: {
    [Language.English]: 'DDMMYYXXXZZ',
    [Language.German]: 'DDMMÅÅXXXZZ',
  },
};

export const getPersonalNumberPlaceholder = (
  market: CountryCode,
  language: Language
): string | undefined => {
  return PersonalNumberPlaceholders[market][language];
};

const DatePlaceholders: MarketLanguagePartial = {
  [CountryCode.BE]: {
    [Language.English]: 'DD/MM/YYYY',
    [Language.French]: 'DD/MM/AAAA',
    [Language.DutchBE]: 'DD/MM/JJJJ',
  },
  [CountryCode.DK]: {
    [Language.Danish]: 'DD/MM/YYYY',
    [Language.English]: 'DD/MM/YYYY',
  },
  [CountryCode.FI]: {
    [Language.English]: 'DD/MM/YYYY',
    [Language.Finnish]: 'DD/MM/YYYY',
  },
  [CountryCode.SE]: {
    [Language.English]: 'DD/MM/YYYY',
    [Language.Swedish]: 'DD/MM/ÅÅÅÅ',
  },
  [CountryCode.NO]: {
    [Language.English]: 'DD/MM/YYYY',
    [Language.Norwegian]: 'DD/MM/YYYY',
  },
  [CountryCode.NL]: {
    [Language.English]: 'DD-MM-YYYY',
    [Language.Dutch]: 'DD-MM-JJJJ',
  },
  [CountryCode.DE]: {
    [Language.English]: 'DD-MM-YYYY',
    [Language.German]: 'DD-MM-JJJJ',
  },
};

export const getDatePlaceholder = (market: CountryCode, language: Language) =>
  DatePlaceholders[market][language];

export const policyTermsUrl: MarketLanguagePartial = {
  [CountryCode.BE]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.BE][Language.English]
    }/info/privacyverklaring-be`,
    [Language.French]: `${
      homePageByMarket[CountryCode.BE][Language.French]
    }/info/declaration-de-confidentialite`,
    [Language.DutchBE]: `${
      homePageByMarket[CountryCode.BE][Language.DutchBE]
    }/info/privacyverklaring-be`,
  },
  [CountryCode.DK]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.DK][Language.English]
    }/info/privacy-notice`,
    [Language.Danish]: `${
      homePageByMarket[CountryCode.DK][Language.Danish]
    }/info/privacy-notice`,
  },
  [CountryCode.FI]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.FI][Language.English]
    }/info/privacy-policy`,
    [Language.Finnish]: `${
      homePageByMarket[CountryCode.FI][Language.Finnish]
    }/info/yksityisyyden-suoja`,
  },
  [CountryCode.SE]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.SE][Language.English]
    }/info/privacy-notice`,
    [Language.Swedish]: `${
      homePageByMarket[CountryCode.SE][Language.Swedish]
    }/info/integritet`,
  },
  [CountryCode.NO]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.NO][Language.English]
    }/info/privacy-policy`,
    [Language.Norwegian]: `${
      homePageByMarket[CountryCode.NO][Language.Norwegian]
    }/info/integritet`,
  },
  [CountryCode.NL]: {
    [Language.Dutch]: `${
      homePageByMarket[CountryCode.NL][Language.Dutch]
    }/info/qeld-privacybeleid`,
  },
  [CountryCode.DE]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.DE][Language.English]
    }/info/privacy-notice`,
    [Language.German]: `${
      homePageByMarket[CountryCode.DE][Language.German]
    }/info/datenschutzerklarung`,
  },
};

export const moneyLaunderingUrl: MarketLanguagePartial = {
  [CountryCode.BE]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.BE][Language.English]
    }/info/witwassen-be`,
    [Language.French]: `${
      homePageByMarket[CountryCode.BE][Language.French]
    }/info/blanchiment-d-argent`,
    [Language.DutchBE]: `${
      homePageByMarket[CountryCode.BE][Language.DutchBE]
    }/info/witwassen-be`,
  },
  [CountryCode.DK]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.DK][Language.English]
    }/info/anti-money-laundering`,
    [Language.Danish]: `${
      homePageByMarket[CountryCode.DK][Language.Danish]
    }/info/hvidvask`,
  },
  [CountryCode.FI]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.FI][Language.English]
    }/info/rahanpesu`,
    [Language.Finnish]: `${
      homePageByMarket[CountryCode.FI][Language.Finnish]
    }/info/rahanpesu`,
  },
  [CountryCode.SE]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.SE][Language.English]
    }/info/anti-money-laundering`,
    [Language.Swedish]: `${
      homePageByMarket[CountryCode.SE][Language.Swedish]
    }/info/penningtvatt`,
  },
  [CountryCode.NO]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.NO][Language.English]
    }/info/anti-money-laundering`,
    [Language.Norwegian]: `${
      homePageByMarket[CountryCode.NO][Language.Norwegian]
    }/info/hvitvasking`,
  },
  [CountryCode.NL]: {
    [Language.Dutch]: `${
      homePageByMarket[CountryCode.NL][Language.Dutch]
    }/info/witwassen-nl`,
  },
  [CountryCode.DE]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.DE][Language.English]
    }/info/money-laundering-and-terrorist-financing`,
    [Language.German]: `${
      homePageByMarket[CountryCode.DE][Language.German]
    }/info/geldwasche-und-terrorismusfinanzierung`,
  },
};

export const supportUrl: MarketLanguagePartial = {
  [CountryCode.BE]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.BE][Language.English]
    }/support`,
    [Language.French]: `${
      homePageByMarket[CountryCode.BE][Language.French]
    }/support`,
    [Language.DutchBE]: `${
      homePageByMarket[CountryCode.BE][Language.DutchBE]
    }/support`,
  },
  [CountryCode.DK]: {
    [Language.Danish]: `${
      homePageByMarket[CountryCode.DK][Language.Danish]
    }/hjaelp`,
    [Language.English]: `${
      homePageByMarket[CountryCode.DK][Language.English]
    }/support`,
  },
  [CountryCode.FI]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.FI][Language.English]
    }/support`,
    [Language.Finnish]: `${
      homePageByMarket[CountryCode.FI][Language.Finnish]
    }/asiakaspalvelu`,
  },
  [CountryCode.SE]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.SE][Language.English]
    }/support`,
    [Language.Swedish]: `${
      homePageByMarket[CountryCode.SE][Language.Swedish]
    }/support`,
  },
  [CountryCode.NO]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.NO][Language.English]
    }/support`,
    [Language.Norwegian]: `${
      homePageByMarket[CountryCode.NO][Language.Norwegian]
    }/support`,
  },
  [CountryCode.NL]: {
    [Language.Dutch]: `${
      homePageByMarket[CountryCode.NL][Language.Dutch]
    }/support`,
  },
  [CountryCode.DE]: {
    [Language.English]: `${
      homePageByMarket[CountryCode.DE][Language.English]
    }/support`,
    [Language.German]: `${
      homePageByMarket[CountryCode.DE][Language.German]
    }/support`,
  },
};

export const buildNewLoanUrl = (
  path: string,
  market: CountryCode,
  language: Language
) => {
  const baseUrl = homePageByMarket[market][language];
  if (!baseUrl) throw new Error(`No base URL found for market: ${market}`);

  const url = new URL(baseUrl);
  url.pathname += url.pathname === '/' ? path : `/${path}`;
  const isQeld = market === CountryCode.NL;
  const utmSource = isQeld ? 'myqeld' : 'myqred';
  const utmContent = isQeld ? 'my-qeld' : 'my-qred';
  const utmCampaignPrefix = isQeld ? 'myqe' : 'myqr';
  let languageParamValue = language;
  if (market === CountryCode.BE) {
    languageParamValue =
      language === Language.DutchBE ? Language.Dutch : language;
  }

  url.searchParams.append('utm_medium', 'login');
  url.searchParams.append('utm_term', 'login');
  url.searchParams.append('utm_source', utmSource);
  url.searchParams.append('utm_content', utmContent);

  url.searchParams.append(
    'utm_campaign',
    `${utmCampaignPrefix}_log_${market}_${languageParamValue}_none_ln_all_all_`
  );

  return url.href.toLowerCase();
};

export const newLoanUrl: MarketLanguagePartial = {
  [CountryCode.SE]: {
    [Language.Swedish]: buildNewLoanUrl(
      'foretagslan',
      CountryCode.SE,
      Language.Swedish
    ),
    [Language.English]: buildNewLoanUrl(
      'business-loan',
      CountryCode.SE,
      Language.English
    ),
  },
  [CountryCode.DK]: {
    [Language.Danish]: buildNewLoanUrl(
      'erhvervslan',
      CountryCode.DK,
      Language.Danish
    ),
    [Language.English]: buildNewLoanUrl(
      'business-loan',
      CountryCode.DK,
      Language.English
    ),
  },
  [CountryCode.FI]: {
    [Language.Finnish]: buildNewLoanUrl(
      'yrityslaina',
      CountryCode.FI,
      Language.Finnish
    ),
    [Language.English]: buildNewLoanUrl(
      'business-loan',
      CountryCode.FI,
      Language.English
    ),
  },
  [CountryCode.BE]: {
    [Language.DutchBE]: buildNewLoanUrl(
      'zakelijke-lening',
      CountryCode.BE,
      Language.DutchBE
    ),
    [Language.French]: buildNewLoanUrl(
      'pret-professionnel',
      CountryCode.BE,
      Language.French
    ),
    [Language.English]: buildNewLoanUrl(
      'zakelijke-lening',
      CountryCode.BE,
      Language.English
    ),
  },
  [CountryCode.NO]: {
    [Language.Norwegian]: buildNewLoanUrl(
      'bedriftslan',
      CountryCode.NO,
      Language.Norwegian
    ),
    [Language.English]: buildNewLoanUrl(
      'bedriftslan',
      CountryCode.NO,
      Language.English
    ),
  },
  [CountryCode.NL]: {
    [Language.Dutch]: buildNewLoanUrl(
      'zakelijke-lening',
      CountryCode.NL,
      Language.Dutch
    ),
  },
  [CountryCode.DE]: {
    [Language.German]: buildNewLoanUrl(
      'firmenkredit',
      CountryCode.DE,
      Language.German
    ),
    [Language.English]: buildNewLoanUrl(
      'business-loan',
      CountryCode.DE,
      Language.English
    ),
  },
};

export const PhoneNumberPlaceholders: MarketValues<string> = {
  [CountryCode.BE]: '+32-XXXXXXXXX',
  [CountryCode.DK]: 'XXXXXXXXX',
  [CountryCode.FI]: '+358 xx xxx xxxx',
  [CountryCode.NL]: 'XXXXXXXXXX',
  [CountryCode.SE]: '+46 XXX XXX XXX',
  [CountryCode.NO]: 'XXX-XX-XXX',
  [CountryCode.DE]: '',
};

export const phoneNumberCountryCodes: MarketValues<string> = {
  [CountryCode.BE]: '+32',
  [CountryCode.DK]: '+45',
  [CountryCode.FI]: '+358',
  [CountryCode.NL]: '+31',
  [CountryCode.SE]: '+46',
  [CountryCode.NO]: '+47',
  [CountryCode.DE]: '+49',
};

export const CompanyTitles: MarketValues<string> = {
  [CountryCode.BE]: 'Qred',
  [CountryCode.DK]: 'Qred',
  [CountryCode.FI]: 'Qred',
  [CountryCode.NL]: 'Qeld',
  [CountryCode.SE]: 'Qred',
  [CountryCode.NO]: 'Qred',
  [CountryCode.DE]: 'Qred',
};

const soleTraderPerMarket: Record<CountryCode, string[]> = {
  [CountryCode.SE]: ['EF', 'Enskild firma'],
  [CountryCode.FI]: [
    'TMI',
    'YEH',
    'YE',
    'Yksityinen elinkeinonharjoittaja',
    'TOIM',
    'Toiminimi',
  ],
  [CountryCode.NO]: ['ENK', 'Enkeltpersonsforetak'],
  [CountryCode.BE]: ['EZ'],
  [CountryCode.NL]: ['EZ'],
  [CountryCode.DK]: [
    'ENK',
    'Enkeltmandsvirksomhed',
    'PMV',
    'Personligt ejet mindre virksomhed',
    'EV',
    'PMV',
  ],
  [CountryCode.DE]: [''],
};

export const companyIsSoleTrader = (
  market: CountryCode,
  companyType: string | null
) =>
  !!companyType &&
  soleTraderPerMarket[market].some(
    (type) => type.toUpperCase() === companyType.toUpperCase()
  );

const stockCompanyTypesPerMarket: Record<CountryCode, string[]> = {
  [CountryCode.SE]: ['ab'],
  [CountryCode.FI]: ['oy'],
  [CountryCode.NO]: ['as'],
  [CountryCode.BE]: ['bv', 'bvba', 'srl', 'sprl'],
  [CountryCode.NL]: ['bv'],
  [CountryCode.DK]: ['aps'],
  [CountryCode.DE]: [''],
};

export const companyIsStockCompany = (
  market: CountryCode,
  companyType: string | null
) =>
  !!companyType &&
  stockCompanyTypesPerMarket[market].some(
    (type) => type.toUpperCase() === companyType.toUpperCase()
  );

const marketsWithOnlyDigitPersonalNumbers: CountryCode[] = [CountryCode.SE];

export const personalNumberIsOnlyNumbers = (market: CountryCode) =>
  marketsWithOnlyDigitPersonalNumbers.includes(market);

export const trustPilotBusinessIdPerMarket: Record<CountryCode, string> = {
  SE: '55c351d80000ff000581d886',
  FI: '5a5dbad77e4c580001eb26e1',
  NL: '5ef08c0bc762fd00019a09a1',
  NO: '62668430b5f1fbd1ead9f157',
  DK: '59b2a60e0000ff0005aab3de',
  BE: '601a6f428668050001e90792',
  DE: '66d1bf8d99801c33b2f2bb98',
};

const marketsWithPersonalNumber: CountryCode[] = [
  CountryCode.SE,
  CountryCode.BE,
  CountryCode.DK,
  CountryCode.FI,
  CountryCode.NO,
];

export const getMarketHasPersonalNumber = (market: CountryCode) =>
  marketsWithPersonalNumber.includes(market);

export const marketHasPersonalNumber: Partial<Record<CountryCode, true>> = {
  [CountryCode.SE]: true,
  [CountryCode.BE]: true,
  [CountryCode.DK]: true,
  [CountryCode.FI]: true,
  [CountryCode.NO]: true,
};

const marketsWithAddress: CountryCode[] = [CountryCode.NL, CountryCode.DE];

export const getMarketHasAddress = (market: CountryCode) =>
  marketsWithAddress.includes(market);

export const marketHasAddress: Partial<Record<CountryCode, true>> = {
  [CountryCode.NL]: true,
  [CountryCode.DE]: true,
};

const marketsHasAddGuarantorButtonNonFooter: Array<CountryCode> = [
  CountryCode.SE,
  CountryCode.BE,
  CountryCode.DK,
  CountryCode.FI,
  CountryCode.NO,
];

export const getMarketsHasAddGuarantorButtonNonFooter = (market: CountryCode) =>
  marketsHasAddGuarantorButtonNonFooter.includes(market);

const marketWithAddGuarantorButtonCopy: CountryCode[] = [CountryCode.NL];

export const getMarketHasAddGuarantorButtonCopy = (market: CountryCode) =>
  marketWithAddGuarantorButtonCopy.includes(market);

const marketOnlyAllowsBoardMemberAsGuarantor: CountryCode[] = [
  CountryCode.NL,
  CountryCode.DE,
];

export const getMarketOnlyAllowsBoardMemberAsGuarantor = (
  market: CountryCode
) => marketOnlyAllowsBoardMemberAsGuarantor.includes(market);

const marketOnlyHasEmailLogin: CountryCode[] = [CountryCode.NL];

export const getMarketOnlyHasEmailLogin = (market: CountryCode) =>
  marketOnlyHasEmailLogin.includes(market);

const marketShouldAskApplicantForFullName: CountryCode[] = [
  CountryCode.NL,
  CountryCode.BE,
];

export const getMarketShouldAskApplicantForFullName = (market: CountryCode) =>
  marketShouldAskApplicantForFullName.includes(market);

export const marketShouldAskLoanApplicantForFirstAndLastName: Partial<
  Record<CountryCode, true>
> = {
  [CountryCode.DE]: true,
  [CountryCode.NL]: true,
  [CountryCode.BE]: true,
};

const marketsHaveMandatoryTurnover: CountryCode[] = [CountryCode.NL];

export const getMarketHasMandatoryTurnover = (market: CountryCode) =>
  marketsHaveMandatoryTurnover.includes(market);

export const marketShouldShowCreditCheckCopyTooltip: Partial<
  Record<CountryCode, true>
> = {
  [CountryCode.SE]: true,
  [CountryCode.NO]: true,
};

export const getHomePageByMarket = (market: CountryCode, language: Language) =>
  homePageByMarket?.[market]?.[language] || 'https://www.qred.com';

export const LoanAmountDefaultValues: Record<CountryCode, IValueDefaults> = {
  [CountryCode.SE]: {
    clcMin: 10000,
    min: 50000,
    max: 5000000,
    step: 10000,
  },
  [CountryCode.NO]: {
    clcMin: 10000,
    min: 50000,
    max: 5000000,
    step: 10000,
  },
  [CountryCode.NL]: {
    clcMin: 1000,
    min: 3000,
    max: 500000,
    step: 1000,
  },
  [CountryCode.FI]: {
    clcMin: 1000,
    min: 5000,
    max: 500000,
    step: 1000,
  },
  [CountryCode.DK]: {
    clcMin: 10000,
    min: 10000,
    max: 3000000,
    step: 10000,
  },
  [CountryCode.BE]: {
    clcMin: 1000,
    min: 1000,
    max: 500000,
    step: 1000,
  },
  [CountryCode.DE]: {
    clcMin: 5000,
    min: 5000,
    max: 500000,
    step: 1000,
  },
};

const marketHasGuarantorSubheader = [CountryCode.BE];
export const getMarketHasGuarantorSubheader = (market: CountryCode) =>
  marketHasGuarantorSubheader.includes(market);

const marketShouldShowAccountNumberInputFieldByDefault: Array<CountryCode> = [
  CountryCode.BE,
  CountryCode.DK,
  CountryCode.FI,
  CountryCode.NO,
  CountryCode.NL,
  CountryCode.DE,
];

export const getMarketShouldShowAccountNumberInputFieldByDefault = (
  market: CountryCode
) => marketShouldShowAccountNumberInputFieldByDefault.includes(market);

const marketHasRoaring: CountryCode[] = [
  CountryCode.SE,
  CountryCode.DK,
  CountryCode.NO,
  CountryCode.FI,
];

export const getMarketHasRoaring = (market: CountryCode) =>
  marketHasRoaring.includes(market);

const marketShouldFetchBeneficialOwners: CountryCode[] = [CountryCode.DE];
export const getMarketShouldFetchBeneficialOwners = (market: CountryCode) =>
  marketShouldFetchBeneficialOwners.includes(market);

const marketRequiresPersonalNumberForFetchedOwners: CountryCode[] = [
  CountryCode.SE,
];

export const getMarketRequiresPersonalNumberForFetchedOwners = (
  market: CountryCode
) => marketRequiresPersonalNumberForFetchedOwners.includes(market);

const marketRequiresDateOfBirthForFetchedOwners: CountryCode[] = [
  CountryCode.FI,
  CountryCode.DE,
];

export const getMarketRequiresDateOfBirthForFetchedOwners = (
  market: CountryCode
) => marketRequiresDateOfBirthForFetchedOwners.includes(market);

const marketRequiresYearOfBirthForFetchedOwners: CountryCode[] = [
  CountryCode.NO,
];

export const getMarketRequiresYearOfBirthForFetchedOwners = (
  market: CountryCode
) => marketRequiresYearOfBirthForFetchedOwners.includes(market);

export const marketHasDateOfBirth: Partial<Record<CountryCode, true>> = {
  [CountryCode.NL]: true,
  [CountryCode.DE]: true,
};

const marketRequiresCountryOfResidenceForFetchedOwners: CountryCode[] = [
  CountryCode.SE,
  CountryCode.DK,
  CountryCode.FI,
  CountryCode.DE,
];

export const getMarketRequiresCountryOfResidenceForFetchedOwners = (
  market: CountryCode
) => marketRequiresCountryOfResidenceForFetchedOwners.includes(market);

const marketHasFetchedOwnershipSharesAsInteger: CountryCode[] = [
  CountryCode.DK,
  CountryCode.NO,
  CountryCode.FI,
  CountryCode.DE,
];

export const getMarketHasFetchedOwnershipSharesAsInteger = (
  market: CountryCode
) => marketHasFetchedOwnershipSharesAsInteger.includes(market);

const marketHasFetchedOwnershipSharesAsRanges = [CountryCode.SE];

export const getMarketHasFetchedOwnershipSharesAsRanges = (
  market: CountryCode
) => marketHasFetchedOwnershipSharesAsRanges.includes(market);

const marketHasMultipleProducts = [CountryCode.SE, CountryCode.FI];

export const getMarketHasMultipleProducts = (market: CountryCode) =>
  marketHasMultipleProducts.includes(market);

export const marketHasTrustpilot: Partial<Record<CountryCode, true>> = {
  [CountryCode.BE]: true,
  [CountryCode.DK]: true,
  [CountryCode.FI]: true,
  [CountryCode.NO]: true,
  [CountryCode.NL]: true,
  [CountryCode.SE]: true,
  [CountryCode.DE]: true,
};

export const termsAndConditionPdfSources: MarketValues<{
  qred: string;
  nordiska: string;
}> = {
  [CountryCode.NL]: {
    qred: '/nl_loan_toc_qeld.pdf',
    nordiska: '/nl_loan_toc_nordiska.pdf',
  },
  [CountryCode.SE]: {
    qred: '',
    nordiska: '',
  },
  [CountryCode.FI]: {
    qred: '/fi_loan_toc_qred.pdf',
    nordiska: '/fi_loan_toc_nordiska.pdf',
  },
  [CountryCode.NO]: {
    // Norway uses external links instead of PDF modals (see useLoanLinks hook)
    qred: '',
    nordiska: '',
  },
  [CountryCode.DK]: {
    qred: '/dk_loan_toc_qred.pdf',
    nordiska: '/dk_loan_toc_nordiska.pdf',
  },
  [CountryCode.BE]: {
    qred: '/be_loan_toc_qred.pdf',
    nordiska: '',
  },
  [CountryCode.DE]: {
    qred: '',
    nordiska: '',
  },
};

export const MARKETS_WITH_EXTERNAL_LOAN_TERMS_LINKS = [
  CountryCode.NO,
  CountryCode.DE,
  CountryCode.SE,
];

// Card markets on the new ConsentText keys; the rest fall back to legacy
// Text3/Link1. Migrating a market also adds its business-products general-terms link.
export const MARKETS_WITH_MIGRATED_CARD_TERMS_KEYS = [
  CountryCode.SE,
  CountryCode.DE,
];

export const marketIso3Code: MarketValues<string> = {
  [CountryCode.SE]: 'SWE',
  [CountryCode.FI]: 'FIN',
  [CountryCode.NO]: 'NOR',
  [CountryCode.DK]: 'DNK',
  [CountryCode.BE]: 'BEL',
  [CountryCode.NL]: 'NLD',
  [CountryCode.DE]: 'DEU',
};

export const marketHasCreditCheckConsentDisclosure: Partial<
  Record<CountryCode, true>
> = {
  [CountryCode.DE]: true,
};

export const getMarketHasCreditCheckConsentDisclosure = (market: CountryCode) =>
  !!marketHasCreditCheckConsentDisclosure[market];

const marketHasMarketingConsentDisclosure = [CountryCode.DE];

export const getMarketHasMarketingConsentDisclosure = (market: CountryCode) =>
  marketHasMarketingConsentDisclosure.includes(market);

export const companyCreditCheckAgencies: MarketValues<
  { label: string; pdfPath?: string; url?: string; id: string }[]
> = {
  [CountryCode.DE]: [
    {
      id: 'schufa',
      label: 'SCHUFA Holding AG',
      pdfPath: '/company_credit_check_schufa_de.pdf',
    },
    {
      id: 'creditreform',
      label: 'Creditreform',
      url: 'https://www.creditreform.com/',
    },
  ],
  [CountryCode.BE]: [],
  [CountryCode.DK]: [],
  [CountryCode.FI]: [],
  [CountryCode.NL]: [],
  [CountryCode.NO]: [],
  [CountryCode.SE]: [],
};

const marketCanSearchCompanyByName = [
  CountryCode.SE,
  CountryCode.FI,
  CountryCode.DK,
  CountryCode.NL,
  CountryCode.BE,
  CountryCode.DE,
];
export const getMarketCanSearchCompanyByName = (market: CountryCode) =>
  marketCanSearchCompanyByName.includes(market);

const marketOnlyAllowsIbanAccountNumbers: CountryCode[] = [
  CountryCode.BE,
  CountryCode.NL,
  CountryCode.FI,
  CountryCode.DE,
];

export const getMarketOnlyAllowsIbanAccountNumbers = (market: CountryCode) =>
  marketOnlyAllowsIbanAccountNumbers.includes(market);

export const connectBankTermsAndConditionsLink: MarketLanguagePartial = {
  [CountryCode.SE]: {
    [Language.Swedish]: `${
      homePageByMarket[CountryCode.SE][Language.Swedish]
    }/info/konto-information-tjanst`,
    [Language.English]: `${
      homePageByMarket[CountryCode.SE][Language.English]
    }/info/account-information-service`,
  },
  [CountryCode.FI]: {
    [Language.Finnish]: `${
      homePageByMarket[CountryCode.FI][Language.Finnish]
    }/info/tilitietopalvelu`,
    [Language.English]: `${
      homePageByMarket[CountryCode.FI][Language.English]
    }/info/terms-account-information-service`,
  },
  [CountryCode.NO]: {
    [Language.Norwegian]: `${
      homePageByMarket[CountryCode.NO][Language.Norwegian]
    }/info/kontoinformasjonstjeneste`,
    [Language.English]: `${
      homePageByMarket[CountryCode.NO][Language.English]
    }/info/account-information-service`,
  },
  [CountryCode.DK]: {
    [Language.Danish]: `${
      homePageByMarket[CountryCode.DK][Language.Danish]
    }/info/konto-information-service`,
    [Language.English]: `${
      homePageByMarket[CountryCode.DK][Language.English]
    }/info/terms-account-information-service`,
  },
  [CountryCode.BE]: {
    [Language.DutchBE]: `${
      homePageByMarket[CountryCode.BE][Language.DutchBE]
    }/info/account-informatie-service`,
    [Language.French]: `${
      homePageByMarket[CountryCode.BE][Language.French]
    }/info/conditions-utilisation-service-information-comptes`,
    [Language.English]: `${
      homePageByMarket[CountryCode.BE][Language.English]
    }/info/account-information-terms-of-use`,
  },
  [CountryCode.NL]: {
    [Language.Dutch]: `${
      homePageByMarket[CountryCode.NL][Language.Dutch]
    }/info/account-informatie-service`,
  },
  [CountryCode.DE]: {
    [Language.German]: `${
      homePageByMarket[CountryCode.DE][Language.German]
    }/info/kontoinformationsdienst`,
    [Language.English]: `${
      homePageByMarket[CountryCode.DE][Language.English]
    }/info/kontoinformationsdienst`,
  },
};

const marketHasEskat = [CountryCode.DK];
export const getMarketHasEskat = (market: CountryCode) =>
  marketHasEskat.includes(market);

export const marketHasCheckpointStep: Record<CountryCode, boolean> = {
  [CountryCode.NL]: true,
  [CountryCode.SE]: true,
  [CountryCode.FI]: true,
  [CountryCode.NO]: true,
  [CountryCode.DK]: true,
  [CountryCode.BE]: true,
  [CountryCode.DE]: false,
};

const marketAlwaysShowManualBankInputOption: Record<CountryCode, boolean> = {
  [CountryCode.NL]: false,
  [CountryCode.SE]: false,
  [CountryCode.FI]: false,
  [CountryCode.NO]: false,
  [CountryCode.DK]: false,
  [CountryCode.BE]: false,
  [CountryCode.DE]: true,
};
export const getMarketAlwaysShowManualBankInputOption = (market: CountryCode) =>
  marketAlwaysShowManualBankInputOption[market];

const cardOfferWelcomeSplashImage: MarketValues<{
  mobile: string;
  desktop: string;
}> = {
  [CountryCode.FI]: {
    mobile: '/QredCardWelcomeImageMobileOld.png',
    desktop: '/QredCardWelcomeImageDesktopOld.png',
  },
  [CountryCode.SE]: {
    mobile: '/QredCardWelcomeImageMobileOld.png',
    desktop: '/QredCardWelcomeImageDesktopOld.png',
  },
  [CountryCode.NL]: {
    mobile: '/QeldCardWelcomeImageMobile.png',
    desktop: '/QeldCardWelcomeImageDesktop.png',
  },
  [CountryCode.DK]: {
    mobile: '',
    desktop: '',
  },
  [CountryCode.NO]: {
    mobile: '',
    desktop: '',
  },
  [CountryCode.DE]: {
    mobile: '/QredCardWelcomeImageMobile.png',
    desktop: '/QredCardWelcomeImageDesktop.png',
  },
  [CountryCode.BE]: {
    mobile: '',
    desktop: '',
  },
};

export const getCardOfferWelcomeSplashImage = (
  market: CountryCode,
  isMobile: boolean
) => {
  const splashImages = cardOfferWelcomeSplashImage[market];

  if (isMobile) return splashImages.mobile;
  return splashImages.desktop;
};

export type MarketWithCardOptIn = CountryCode.SE | CountryCode.FI;

export const isMarketWithCardOptIn = (
  market: CountryCode
): market is MarketWithCardOptIn =>
  market === CountryCode.SE || market === CountryCode.FI;

export const marketHasBrokerDataSharingDisclosure: Record<
  CountryCode,
  boolean
> = {
  [CountryCode.SE]: true,
  [CountryCode.FI]: false,
  [CountryCode.NO]: false,
  [CountryCode.DK]: false,
  [CountryCode.BE]: false,
  [CountryCode.NL]: false,
  [CountryCode.DE]: false,
};

export const marketHasCardAutoApproval: Partial<Record<CountryCode, true>> = {
  [CountryCode.DE]: true,
};

export const marketHasSepaDirectDebitFeature: Partial<
  Record<CountryCode, true>
> = {
  [CountryCode.BE]: true,
  [CountryCode.DE]: true,
  [CountryCode.NL]: true,
  [CountryCode.FI]: true,
};

export const isSepaDirectDebitEnabledByDefault: Partial<
  Record<CountryCode, true>
> = {
  [CountryCode.BE]: true,
  [CountryCode.DE]: true,
  [CountryCode.NL]: true,
};

// Markets and routes where SVC onboarding auth is supported
const SVC_ONBOARDING_AUTH_SUPPORTED = [
  { market: CountryCode.DE, routeFragment: constVars.ROUTE_CARD_OFFER },
];

export const marketSupportsSvcOnboardingAuth = (
  market: CountryCode,
  pathname: string
) =>
  SVC_ONBOARDING_AUTH_SUPPORTED.some(
    (entry) => entry.market === market && pathname.includes(entry.routeFragment)
  );
